/** Who the call is being booked by. The funnel knows them by this point, so the embed should not ask again. */
export type BookingInvitee = {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
};

type BookingProvider = {
  hosts: string[];
  fill: (params: URLSearchParams, invitee: BookingInvitee) => void;
};

/**
 * Which parameter puts each provider's page into embed mode, and how that provider spells prefill. The link is
 * an application setting, so the provider is whoever the sales team happens to use; a host nobody here knows
 * is framed as written.
 */
const PROVIDERS: BookingProvider[] = [
  {
    hosts: ['calendly.com'],
    fill: (params, invitee) => {
      params.set('embed_type', 'Inline');
      // Calendly talks to the framing page over postMessage and needs to be told which origin that is.
      params.set('embed_domain', window.location.hostname);
      prefill(params, { name: fullName(invitee), email: invitee.email });
    },
  },
  {
    hosts: ['cal.com'],
    fill: (params, invitee) => {
      // What turns on the messages the page sends to whoever frames it, this screen's booking one included.
      // The value names an embed instance and a page framed on its own needs no name.
      params.set('embed', '');
      params.set('embedType', 'inline');
      prefill(params, { name: fullName(invitee), email: invitee.email });
    },
  },
  {
    hosts: ['hubspot.com'],
    fill: (params, invitee) => {
      params.set('embed', 'true');
      prefill(params, { firstName: invitee.firstName, lastName: invitee.lastName, email: invitee.email });
    },
  },
  {
    hosts: ['calendar.google.com'],
    // Google fills the guest form from whoever is signed in to Google and reads no prefill from the URL.
    fill: params => params.set('gv', 'true'),
  },
];

/**
 * The link as it should be framed, or null for anything that is not a plain https address — including an
 * empty setting, so the caller has one absence to handle rather than two.
 */
export const bookingEmbedUrl = (
  link: string | null | undefined,
  invitee: BookingInvitee,
): string | null => {
  if (link == null || link.trim().length === 0) return null;

  const url = (() => {
    try {
      return new URL(link.trim());
    } catch {
      return null;
    }
  })();

  // `javascript:` is the reason this rules on the scheme rather than on the text looking like an address.
  if (url == null || url.protocol !== 'https:') return null;

  PROVIDERS
    .find(provider => provider.hosts.some(host => isHost(url.hostname, host)))
    ?.fill(url.searchParams, invitee);

  return url.toString();
};

// What the admin wrote into the link wins: they may be pointing at a page that asks something of its own,
// and an answer of ours would silently replace theirs.
const prefill = (params: URLSearchParams, values: Record<string, string | null>) => {
  Object.entries(values).forEach(([name, value]) => {
    if (value != null && value.length > 0 && !params.has(name)) params.set(name, value);
  });
};

const fullName = (invitee: BookingInvitee): string | null => {
  const name = [invitee.firstName, invitee.lastName]
    .filter((part): part is string => part != null && part.length > 0)
    .join(' ');

  return name.length > 0 ? name : null;
};

const isHost = (hostname: string, host: string): boolean =>
  hostname === host || hostname.endsWith(`.${host}`);

/**
 * Whether a scheduler has just announced a booking to the page framing it. Only the page we framed is
 * listened to — any window on the internet can post here, and this message moves the client on.
 *
 * Calendly, Cal.com and HubSpot each say it in their own words; Google and Microsoft say nothing at all,
 * which is why the way off this screen never depends on hearing it.
 */
export const isCallBookedMessage = (event: MessageEvent, embedUrl: string | null): boolean => {
  if (embedUrl == null || event.origin !== originOf(embedUrl)) return false;

  const data = event.data as {
    event?: unknown;
    originator?: unknown;
    type?: unknown;
    meetingBookSucceeded?: unknown;
  } | null;

  return data?.event === 'calendly.event_scheduled'
    || (data?.originator === 'CAL' && CALCOM_BOOKED_TYPES.includes(data?.type as string))
    || data?.meetingBookSucceeded === true;
};

// `bookingSuccessfulV2` is what Cal.com sends now; the one it deprecates is still listened for.
const CALCOM_BOOKED_TYPES: string[] = ['bookingSuccessfulV2', 'bookingSuccessful'];

const originOf = (url: string): string | null => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

export const MAX_BOOKING_REDIRECT_SECONDS = 30;

/**
 * How long the booking confirmation stays up before the client is moved on, or null for no automatic move.
 * Anything but a whole number of seconds from 0 to `MAX_BOOKING_REDIRECT_SECONDS` counts as unset.
 */
export const bookingRedirectDelayMs = (seconds: unknown): number | null =>
  typeof seconds === 'number'
    && Number.isInteger(seconds)
    && seconds >= 0
    && seconds <= MAX_BOOKING_REDIRECT_SECONDS
    ? seconds * 1000
    : null;
